import React, { useEffect, useState, useRef } from "react";
import $ from "jquery";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState("");
  const [minOrderAmount, setMinOrderAmount] = useState("");
  const [maxDiscountAmount, setMaxDiscountAmount] = useState("");
  const [usageLimit, setUsageLimit] = useState("");
  const [perUserLimit, setPerUserLimit] = useState("1");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("00:00");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("23:59");
  const [isActive, setIsActive] = useState(true);
  const [isPublic, setIsPublic] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "info", msg: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all"); // all, active, expired, inactive, private
  const [lastChecked, setLastChecked] = useState(null);

  const formRef = useRef(null);
  const toastRef = useRef(null);
  const pageRef = useRef(null);

  // Format date for input (handles both 'Y-m-d H:i:s' and 'Y-m-d\TH:i' formats)
  const formatDateForInput = (dateTimeStr) => {
    if (!dateTimeStr) return { date: '', time: '' };
    
    // Handle different date formats
    let datePart = '';
    let timePart = '00:00';
    
    if (dateTimeStr.includes('T')) {
      // ISO format: 2024-01-15T14:30
      const parts = dateTimeStr.split('T');
      datePart = parts[0] || '';
      timePart = parts[1] ? parts[1].substring(0, 5) : '00:00';
    } else if (dateTimeStr.includes(' ')) {
      // MySQL format: 2024-01-15 14:30:00
      const parts = dateTimeStr.split(' ');
      datePart = parts[0] || '';
      timePart = parts[1] ? parts[1].substring(0, 5) : '00:00';
    } else {
      datePart = dateTimeStr;
    }
    
    return {
      date: datePart,
      time: timePart
    };
  };

  // Get full datetime for API
  const getFullDateTime = (date, time) => {
    if (!date) return '';
    return `${date} ${time}:00`; // MySQL format: Y-m-d H:i:s
  };

  // Show toast
  const showToast = (type, msg) => {
    setToast({ show: true, type, msg });
    gsap.fromTo(
      toastRef.current,
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.3, ease: "power2.out" }
    );
    setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
  };

  // Function to check and update expired coupons
  const checkExpiredCoupons = async (showNotification = false) => {
    try {
      const response = await $.ajax({
        url: `${ApiURl}/checkExpiredCoupons.php`,
        type: 'POST',
        dataType: 'json'
      });
      console.log(response)
      if (response?.success) {
        if (response.expired_count > 0 || response.activated_count > 0) {
          console.log(`Updated ${response.expired_count} expired and ${response.activated_count} activated coupons`);
          setLastChecked(new Date().toLocaleTimeString());
          if (showNotification) {
            showToast("success", `Updated ${response.expired_count} expired and ${response.activated_count} activated coupons`);
          }
          // Refresh the coupons list if any changes were made
          await fetchCoupons(false); // Pass false to avoid recursive checking
        } else if (showNotification) {
          showToast("info", "No expired coupons found");
        }
      }
    } catch (error) {
      console.error("Error checking expired coupons:", error);
      if (showNotification) {
        showToast("error", "Failed to check expired coupons");
      }
    }
  };

  // Fetch coupons
  const fetchCoupons = async (checkExpired = true) => {
    setLoading(true);
    try {
      // First check and update expired coupons if needed
      if (checkExpired) {
        await checkExpiredCoupons();
      }
      
      // Then fetch the updated list
      const response = await $.getJSON(`${ApiURl}/getCoupons.php?admin=true`);
      console.log(response)
      if (response?.success) {
        setCoupons(response.coupons || []);
      } else {
        showToast("error", response?.message || "Failed to load coupons");
      }
    } catch (error) {
      console.error("Error fetching coupons:", error);
      showToast("error", "Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
    
    // Set up periodic checking of expired coupons (every minute)
    const intervalId = setInterval(() => {
      checkExpiredCoupons();
    }, 60000); // Check every 60 seconds
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Set default dates
  useEffect(() => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    setStartDate(today);
    
    const nextMonth = new Date(now);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setEndDate(nextMonth.toISOString().split('T')[0]);
  }, []);

  // GSAP animation for form
  useEffect(() => {
    if (showForm && formRef.current) {
      gsap.fromTo(
        formRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }
      );
    }
  }, [showForm]);

  // Reset form
  const resetForm = () => {
    setCode("");
    setDescription("");
    setDiscountType("percentage");
    setDiscountValue("");
    setMinOrderAmount("");
    setMaxDiscountAmount("");
    setUsageLimit("");
    setPerUserLimit("1");
    
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    setStartDate(today);
    setStartTime("00:00");
    
    const nextMonth = new Date(now);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setEndDate(nextMonth.toISOString().split('T')[0]);
    setEndTime("23:59");
    
    setIsActive(true);
    setIsPublic(true);
    setEditingCoupon(null);
    setShowForm(false);
  };

  // Edit coupon
  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setCode(coupon.code);
    setDescription(coupon.description || "");
    setDiscountType(coupon.discount_type);
    setDiscountValue(coupon.discount_value.toString());
    setMinOrderAmount(coupon.min_order_amount?.toString() || "");
    setMaxDiscountAmount(coupon.max_discount_amount?.toString() || "");
    setUsageLimit(coupon.usage_limit?.toString() || "");
    setPerUserLimit(coupon.per_user_limit?.toString() || "1");
    
    const start = formatDateForInput(coupon.start_date);
    setStartDate(start.date);
    setStartTime(start.time);
    
    const end = formatDateForInput(coupon.end_date);
    setEndDate(end.date);
    setEndTime(end.time);
    
    setIsActive(coupon.is_active == 1);
    setIsPublic(coupon.is_public == 1);
    setShowForm(true);
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Delete coupon
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;

    try {
      const response = await $.ajax({
        url: `${ApiURl}/deleteCoupon.php?id=${id}`,
        type: 'DELETE',
        dataType: 'json'
      });

      if (response?.success) {
        showToast("success", "Coupon deleted successfully");
        await fetchCoupons();
      } else {
        showToast("error", response?.message || "Failed to delete coupon");
      }
    } catch (error) {
      console.error("Error deleting coupon:", error);
      showToast("error", "Server error while deleting coupon");
    }
  };

  // Toggle coupon status
  const toggleStatus = async (coupon) => {
    // Check if coupon is expired
    const now = new Date();
    const endDate = new Date(coupon.end_date);
    
    if (endDate < now && !coupon.is_active) {
      showToast("error", "Cannot activate an expired coupon");
      return;
    }
    
    try {
      const formData = {
        code: coupon.code,
        description: coupon.description || "",
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value,
        min_order_amount: coupon.min_order_amount || 0,
        max_discount_amount: coupon.max_discount_amount,
        usage_limit: coupon.usage_limit,
        per_user_limit: coupon.per_user_limit,
        start_date: coupon.start_date,
        end_date: coupon.end_date,
        is_active: coupon.is_active ? 0 : 1,
        is_public: coupon.is_public
      };

      const response = await $.ajax({
        url: `${ApiURl}/updateCoupon.php?id=${coupon.id}`,
        type: 'POST',
        data: JSON.stringify(formData),
        contentType: 'application/json',
        dataType: 'json'
      });

      if (response?.success) {
        showToast("success", `Coupon ${coupon.is_active ? 'deactivated' : 'activated'} successfully`);
        await fetchCoupons();
      } else {
        showToast("error", response?.message || "Failed to update coupon status");
      }
    } catch (error) {
      console.error("Error toggling coupon status:", error);
      showToast("error", "Server error while updating coupon status");
    }
  };

  // Toggle public/private status
  const toggleVisibility = async (coupon) => {
    try {
      const formData = {
        code: coupon.code,
        description: coupon.description || "",
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value,
        min_order_amount: coupon.min_order_amount || 0,
        max_discount_amount: coupon.max_discount_amount,
        usage_limit: coupon.usage_limit,
        per_user_limit: coupon.per_user_limit,
        start_date: coupon.start_date,
        end_date: coupon.end_date,
        is_active: coupon.is_active,
        is_public: coupon.is_public ? 0 : 1
      };

      const response = await $.ajax({
        url: `${ApiURl}/updateCoupon.php?id=${coupon.id}`,
        type: 'POST',
        data: JSON.stringify(formData),
        contentType: 'application/json',
        dataType: 'json'
      });

      if (response?.success) {
        showToast("success", `Coupon is now ${coupon.is_public ? 'private' : 'public'}`);
        await fetchCoupons();
      } else {
        showToast("error", response?.message || "Failed to update coupon visibility");
      }
    } catch (error) {
      console.error("Error toggling coupon visibility:", error);
      showToast("error", "Server error while updating coupon visibility");
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!code.trim()) {
      showToast("error", "Please enter coupon code");
      return;
    }

    if (!discountValue || parseFloat(discountValue) <= 0) {
      showToast("error", "Please enter a valid discount value");
      return;
    }

    if (discountType === 'percentage' && parseFloat(discountValue) > 100) {
      showToast("error", "Percentage discount cannot exceed 100%");
      return;
    }

    if (!startDate || !endDate) {
      showToast("error", "Please select start and end dates");
      return;
    }

    const startDateTime = getFullDateTime(startDate, startTime);
    const endDateTime = getFullDateTime(endDate, endTime);
    
    const now = new Date();
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    if (end < start) {
      showToast("error", "End date cannot be before start date");
      return;
    }

    // Warn if end date is in the past
    if (end < now) {
      if (!window.confirm("This coupon's end date is in the past. It will be inactive. Continue?")) {
        return;
      }
    }

    setSubmitting(true);

    const formData = {
      code: code.toUpperCase().trim(),
      description: description.trim(),
      discount_type: discountType,
      discount_value: parseFloat(discountValue),
      min_order_amount: minOrderAmount ? parseFloat(minOrderAmount) : 0,
      max_discount_amount: maxDiscountAmount ? parseFloat(maxDiscountAmount) : null,
      usage_limit: usageLimit ? parseInt(usageLimit) : null,
      per_user_limit: parseInt(perUserLimit) || 1,
      start_date: startDateTime,
      end_date: endDateTime,
      is_active: isActive ? 1 : 0,
      is_public: isPublic ? 1 : 0
    };

    const url = editingCoupon 
      ? `${ApiURl}/updateCoupon.php?id=${editingCoupon.id}`
      : `${ApiURl}/addCoupon.php`;

    try {
      const response = await $.ajax({
        url: url,
        type: "POST",
        data: JSON.stringify(formData),
        contentType: "application/json",
        dataType: "json"
      });

      if (response?.success) {
        showToast("success", editingCoupon ? "Coupon updated successfully" : "Coupon added successfully");
        resetForm();
        await fetchCoupons();
      } else {
        showToast("error", response?.message || "Failed to save coupon");
      }
    } catch (error) {
      console.error("Error saving coupon:", error);
      showToast("error", "Server error while saving coupon");
    } finally {
      setSubmitting(false);
    }
  };

  // Filter coupons
  // Filter coupons - UPDATE THIS FUNCTION
const filteredCoupons = coupons.filter(coupon => {
  const matchesSearch = coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (coupon.description && coupon.description.toLowerCase().includes(searchTerm.toLowerCase()));
  
  // Compute validity based on dates
  const now = new Date();
  const startDate = new Date(coupon.start_date);
  const endDate = new Date(coupon.end_date);
  const isWithinDateRange = startDate <= now && endDate >= now;
  const isExpired = endDate < now;
  const isValid = coupon.is_active === 1 && isWithinDateRange;
  
  if (filter === 'all') return matchesSearch;
  if (filter === 'active') return matchesSearch && isValid;
  if (filter === 'expired') return matchesSearch && isExpired;
  if (filter === 'inactive') return matchesSearch && coupon.is_active === 0 && !isExpired;
  if (filter === 'public') return matchesSearch && coupon.is_public === 1 && isValid;
  if (filter === 'private') return matchesSearch && coupon.is_public === 0;
  return matchesSearch;
});

// Update the getStatusBadge function
const getStatusBadge = (coupon) => {
  const now = new Date();
  const endDate = new Date(coupon.end_date);
  const isExpired = endDate < now;
  const isWithinDateRange = new Date(coupon.start_date) <= now && endDate >= now;
  const isValid = coupon.is_active === 1 && isWithinDateRange;
  
  if (isValid) {
    return {
      bg: 'bg-green-50',
      text: 'text-green-700',
      border: 'border-green-200',
      label: 'Active'
    };
  } else if (isExpired) {
    return {
      bg: 'bg-red-50',
      text: 'text-red-700',
      border: 'border-red-200',
      label: 'Expired'
    };
  } else {
    return {
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      border: 'border-slate-200',
      label: coupon.is_active === 1 ? 'Scheduled' : 'Inactive'
    };
  }
};



  // Get visibility badge
  const getVisibilityBadge = (isPublic) => {
    return isPublic == 1 ? {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
      label: 'Public'
    } : {
      bg: 'bg-purple-50',
      text: 'text-purple-700',
      border: 'border-purple-200',
      icon: 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21',
      label: 'Private'
    };
  };

  return (
    <div ref={pageRef} className="w-full font-['Poppins',sans-serif] bg-gray-50 min-h-screen">
      {/* Toast */}
      {toast.show && (
        <div
          ref={toastRef}
          className="fixed bottom-5 right-5 z-[9999] max-w-[90vw] sm:max-w-md"
        >
          <div
            className={[
              "rounded-xl px-4 py-3 shadow-lg border text-sm",
              toast.type === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : toast.type === "error"
                ? "bg-rose-50 border-rose-200 text-rose-900"
                : "bg-slate-50 border-slate-200 text-slate-900",
            ].join(" ")}
          >
            {toast.msg}
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
        {/* Header */}
        {/* Header */}
<div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
  <div>
    <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
      Coupon Management
    </h1>
    <p className="text-sm text-slate-600 mt-1">
      Create and manage discount coupons for your store
    </p>
    {lastChecked && (
      <p className="text-xs text-slate-500 mt-1">
        Last checked for expired: {lastChecked}
      </p>
    )}
  </div>
  
  {/* ADD THIS BUTTON HERE */}
  <button
    onClick={() => {
      resetForm();
      setShowForm(!showForm);
    }}
    className="px-4 py-2.5 bg-[#b29063] text-white rounded-xl hover:bg-[#a08053] transition flex items-center gap-2 cursor-pointer whitespace-nowrap"
  >
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
    </svg>
    {showForm ? 'Close Form' : 'Add New Coupon'}
  </button>
</div>

        {/* Form */}
        {showForm && (
          <div ref={formRef} className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-8">
            <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
              <h2 className="text-lg font-semibold text-slate-900">
                {editingCoupon ? "Edit Coupon" : "Add New Coupon"}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  {/* Coupon Code */}
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">
                      Coupon Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                      placeholder="e.g., SUMMER20"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">
                      Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows="3"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                      placeholder="Coupon description (optional)"
                    />
                  </div>

                  {/* Discount Type */}
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">
                      Discount Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={discountType}
                      onChange={(e) => setDiscountType(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (₹)</option>
                    </select>
                  </div>

                  {/* Discount Value */}
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">
                      Discount Value <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={discountType === 'percentage' ? 100 : undefined}
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                      placeholder={discountType === 'percentage' ? "e.g., 20" : "e.g., 500"}
                      required
                    />
                  </div>

                  {/* Min Order Amount */}
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">
                      Minimum Order Amount
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={minOrderAmount}
                      onChange={(e) => setMinOrderAmount(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                      placeholder="Leave empty for no minimum"
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  {/* Max Discount Amount (for percentage) */}
                  {discountType === 'percentage' && (
                    <div>
                      <label className="block mb-1 text-sm font-medium text-slate-900">
                        Maximum Discount Amount
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={maxDiscountAmount}
                        onChange={(e) => setMaxDiscountAmount(e.target.value)}
                        className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                        placeholder="Leave empty for no maximum"
                      />
                    </div>
                  )}

                  {/* Usage Limit */}
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">
                      Total Usage Limit
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={usageLimit}
                      onChange={(e) => setUsageLimit(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                      placeholder="Leave empty for unlimited"
                    />
                  </div>

                  {/* Per User Limit */}
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">
                      Per User Limit
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={perUserLimit}
                      onChange={(e) => setPerUserLimit(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">
                      Start Date & Time <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                        required
                      />
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                        required
                      />
                    </div>
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-900">
                      End Date & Time <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                        required
                      />
                      <input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                        required
                      />
                    </div>
                  </div>

                  {/* Status and Visibility */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 text-sm font-medium text-slate-900">Status</label>
                      <div className="flex items-center h-[42px]">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            className="w-4 h-4 rounded border-slate-300 text-[#b29063] focus:ring-[#b29063]"
                          />
                          <span className="text-sm text-slate-700">Active</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block mb-1 text-sm font-medium text-slate-900">Visibility</label>
                      <div className="flex items-center h-[42px]">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-700">Public (visible in cart)</span>
                        </label>
                      </div>
                      {!isPublic && (
                        <p className="text-xs text-purple-600 mt-1">
                          Private coupon - won't be visible in cart
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-4 border-t border-slate-200">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 bg-[#b29063] text-white rounded-xl hover:bg-[#a08053] transition disabled:opacity-50 cursor-pointer"
                >
                  {submitting ? "Saving..." : (editingCoupon ? "Update Coupon" : "Add Coupon")}
                </button>
                
                {editingCoupon && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 transition cursor-pointer"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Filters and Search */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search coupons by code or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition cursor-pointer ${
                filter === 'all'
                  ? 'bg-[#b29063] text-white'
                  : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              All ({coupons.length})
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition cursor-pointer ${
                filter === 'active'
                  ? 'bg-green-600 text-white'
                  : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setFilter('public')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition cursor-pointer ${
                filter === 'public'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              Public
            </button>
            <button
              onClick={() => setFilter('private')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition cursor-pointer ${
                filter === 'private'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              Private
            </button>
            <button
              onClick={() => setFilter('inactive')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition cursor-pointer ${
                filter === 'inactive'
                  ? 'bg-slate-600 text-white'
                  : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              Inactive
            </button>
            <button
              onClick={() => setFilter('expired')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition cursor-pointer ${
                filter === 'expired'
                  ? 'bg-red-600 text-white'
                  : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              Expired
            </button>
          </div>
        </div>

        {/* Coupons List */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Coupons</h2>
            <p className="text-sm text-slate-600 mt-1">
              Showing {filteredCoupons.length} of {coupons.length} coupons
            </p>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#b29063] border-r-transparent"></div>
                <p className="mt-2 text-sm text-slate-600">Loading coupons...</p>
              </div>
            ) : filteredCoupons.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-slate-900">No coupons found</h3>
                <p className="mt-1 text-sm text-slate-500">
                  {searchTerm || filter !== 'all' 
                    ? "Try adjusting your search or filter" 
                    : "Get started by creating a new coupon"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6 sm:-mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Code
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Discount
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Min Order
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Usage
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Valid Until
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Visibility
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {filteredCoupons.map((coupon) => {
                        const status = getStatusBadge(coupon);
                        const visibility = getVisibilityBadge(coupon.is_public);
                        return (
                          <tr key={coupon.id} className="hover:bg-slate-50 transition">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div>
                                  <div className="text-sm font-medium text-[#b29063]">{coupon.code}</div>
                                  {coupon.description && (
                                    <div className="text-xs text-slate-500 max-w-xs truncate">
                                      {coupon.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-slate-900">
                                {coupon.discount_type === 'percentage' 
                                  ? `${coupon.discount_value}%`
                                  : `₹${parseFloat(coupon.discount_value).toFixed(2)}`
                                }
                              </div>
                              {coupon.max_discount_amount && coupon.discount_type === 'percentage' && (
                                <div className="text-xs text-slate-500">
                                  Max: ₹{parseFloat(coupon.max_discount_amount).toFixed(2)}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-slate-900">
                                {coupon.min_order_amount > 0 
                                  ? `₹${parseFloat(coupon.min_order_amount).toFixed(2)}`
                                  : 'No minimum'
                                }
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-slate-900">
                                {coupon.usage_limit 
                                  ? `${coupon.used_count || 0}/${coupon.usage_limit}`
                                  : `${coupon.used_count || 0}/∞`
                                }
                              </div>
                              <div className="text-xs text-slate-500">
                                Per user: {coupon.per_user_limit}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-slate-900">
                                {new Date(coupon.end_date).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}
                              </div>
                              <div className="text-xs text-slate-500">
                                {new Date(coupon.end_date).toLocaleTimeString('en-IN', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer ${status.bg} ${status.text} ${status.border}`}
                                onClick={() => toggleStatus(coupon)}
                                title="Click to toggle status"
                              >
                                {status.label}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer ${visibility.bg} ${visibility.text} ${visibility.border}`}
                                onClick={() => toggleVisibility(coupon)}
                                title={`Click to make ${coupon.is_public ? 'private' : 'public'}`}
                              >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={visibility.icon} />
                                </svg>
                                {visibility.label}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => handleEdit(coupon)}
                                  className="p-2 text-slate-600 hover:text-[#b29063] transition cursor-pointer rounded-lg hover:bg-slate-100"
                                  title="Edit coupon"
                                >
                                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDelete(coupon.id)}
                                  className="p-2 text-slate-600 hover:text-red-600 transition cursor-pointer rounded-lg hover:bg-slate-100"
                                  title="Delete coupon"
                                >
                                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminCoupons;